/*
 * Returns "0.000g" for null/undefined/empty/NaN inputs.
 *
 * @param {*} val - Raw value from orderList.grossWt / netWt (number | string | null)
 * @returns {string} e.g. "220.000g"
 */
export function formatGrossWt(val) {
  if (val === null || val === undefined || val === "") return "0.000g";
  const n = parseFloat(val);
  if (isNaN(n)) return "0.000g";
  return `${n.toFixed(3)}g`;
}
/**
 * Parse a gross weight value to a safe number for arithmetic.
 *
 * Returns 0 for null/undefined/empty/NaN inputs, enabling reliable calculations.
 *
 * @param {*} val - Raw weight value (number | string | null)
 * @returns {number} parseFloat(val) or 0
 */
export function parseGrossWt(val) {
  if (val === null || val === undefined || val === "") return 0;
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}
/**
 * Format a rupee amount as an Indian-currency string with exactly two decimals.
 *
 * Example: 1234.5 → "₹1,234.50"
 *
 * @param {number} total - Amount in rupees
 * @returns {string} Formatted string prefixed with ₹
 */
export function formatRupee(total) {
  return `₹${total.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
/**
 * Compute the grand total cash from an array of seller groups.
 * @param {Array<{totalMc?: number}>} groups - Array of seller groups
 * @returns {number} Grand total rounded to 2 decimal places
 */
export function computeGrandTotalMc(groups) {
  return parseFloat(
    groups.reduce((sum, g) => sum + (g.totalMc || 0), 0).toFixed(2)
  );
}
// ─── Bucket calculation helpers ──────────────────────────────────────────────
export function calcBucket({ metalDue, cashDue, mode, partialMetal, saleRate, purchaseRate }) {
  if (mode === "metal") {
    return {
      metalPayment: parseFloat((metalDue + cashDue / saleRate).toFixed(3)),
      cashPayment: 0,
    };
  }
  if (mode === "cash") {
    return {
      metalPayment: 0,
      cashPayment: parseFloat((cashDue + metalDue * purchaseRate).toFixed(2)),
    };
  }
  let pm = 0;
  if (partialMetal) {
    const num = parseFloat(partialMetal);
    pm = isNaN(num) ? 0 : num;
  }

  if (metalDue >= pm) {
    return {
      metalPayment: pm,
      cashPayment: parseFloat((cashDue + (metalDue - pm) * purchaseRate).toFixed(2)),
    };
  }
  return {
    metalPayment: pm,
    cashPayment: parseFloat((cashDue + (pm - metalDue) * saleRate).toFixed(2)),
  };
}

/**
 * Compute return metrics (fineWeight and totalMc) for a single order piece.
 *
 * @param {Object} order - The order object with specification fields
 * @param {number} netWt - Net weight of the piece being returned
 * @returns {{ fineWeight: number, totalMc: number }}
 */
export function calcReturnMetrics(order, netWt) {
  const n = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const parsed = parseFloat(val);
    return isNaN(parsed) ? 0 : parsed;
  };

  const baseWastage =
    n(order.wastage) +
    n(order.wastageOperator) +
    n(order.wastageJmi) +
    n(order.specificationMC);

  const hasSpecification =
    order.specification &&
    order.specification.toString().trim().toUpperCase() !== "PLANE";

  const specAdditions = hasSpecification
    ? (n(order.specificationMC) * n(order.specificationMakingJmi)) / 100 +
      (n(order.specificationMC) * n(order.specificationMakingOperator)) / 100
    : 0;

  const totalWastage = n(order.buyerDeskCalculation) + baseWastage + specAdditions;

  const setMcBase = n(order.setMc || order.lotDetails?.[0]?.setMc);
  const setMc =
    setMcBase +
    setMcBase * ((n(order.setMakingJmi) + n(order.setMakingOperator)) / 100);

  const gramMcBase = n(order.netGramMc || order.lotDetails?.[0]?.netGramMc);
  const gramMcPerGram =
    gramMcBase +
    gramMcBase *
      ((n(order.gramMakingJmi) + n(order.gramMakingOperator)) / 100);

  const specMcBase = n(
    order.specificationGramRate || order.lotDetails?.[0]?.specificationGramRate
  );
  const specMcPerGram =
    specMcBase +
    specMcBase *
      ((n(order.specificationGramRateJmi) +
        n(order.specificationGramRateOperator)) /
        100);

  const specWt = n(order.specificationWt);
  const stampCostPerSet = n(order.stampCertificateQtyPerSet) * n(order.stampCertificateCost);

  const fineWeight = parseFloat(((netWt * totalWastage) / 100).toFixed(3));
  const totalMc = parseFloat(
    (setMc + gramMcPerGram * netWt + specMcPerGram * specWt + stampCostPerSet).toFixed(2)
  );

  return { fineWeight, totalMc };
}

/**
 * Calculate order-level metrics (fineWeight and totalMc) from product fields.
 *
 * @param {Object} product - Order product object
 * @param {function} num - Safe number parser
 * @returns {{ fineWeight: number, totalMc: number }}
 */
export function calculateOrderMetrics(product, num) {
  const totalWestage =
    num(product.buyerDeskCalculation) +
    num(product.wastage) +
    num(product.wastageOperator) +
    num(product.wastageJmi) +
    num(product.specificationMC) +
    (product.specification && product.specification !== "PLANE"
      ? num(product.specificationMakingJmi) + num(product.specificationMakingOperator)
      : 0);

  const setMcBase = num(product.setMc);
  const setMc =
    setMcBase +
    setMcBase * ((num(product.setMakingJmi) + num(product.setMakingOperator)) / 100);

  const gramMcPerGram =
    num(product.netGramMc) +
    num(product.netGramMc) *
    ((num(product.gramMakingJmi) + num(product.gramMakingOperator)) / 100);

  const specWt = num(product.specificationWt);
  const specMcPerGram =
    num(product.specificationGramRate) +
    num(product.specificationGramRate) *
    ((num(product.specificationGramRateJmi) +
      num(product.specificationGramRateOperator)) /
      100);

  const TotalAmount =
    setMc + gramMcPerGram * num(product.netWt) + specMcPerGram * specWt;
  const fineWeight = (num(product.netWt) * totalWestage) / 100;

  return {
    fineWeight: parseFloat(fineWeight.toFixed(3)),
    totalMc: parseFloat(TotalAmount.toFixed(2)),
  };
}

/**
 * Build a size-entries array from an order's orderDetail object.
 *
 * @param {Object} order - Order containing orderDetail
 * @param {function} num - Safe number parser
 * @returns {Array<{size: string, netwt: number, grossWT: number, maxQty: number, stampCertificateQtyPerSet: number}>}
 */
export function buildSizeEntries(order, num) {
  const orderDetail = order.orderDetail || {};
  return Object.entries(orderDetail).map(([size, detail]) => ({
    size,
    netwt: num(detail.netwt) || 0,
    grossWT: num(detail.grossWT) || 0,
    maxQty: num(detail.qty) || 0,
    stampCertificateQtyPerSet: num(detail.stampCertificateQtyPerSet) || 0,
    returnReason: detail.returnReason || "",
    returnQty: num(detail.returnQty) || 0,
    returnGrossWt: num(detail.returnGrossWt) || 0,
    returnNetWt: num(detail.returnNetWt) || 0,
    finalQty: detail.finalQty !== undefined ? num(detail.finalQty) : num(detail.qty) || 0,
    finalGrossWt: detail.finalGrossWt !== undefined ? num(detail.finalGrossWt) : 0,
    finalNetWt: detail.finalNetWt !== undefined ? num(detail.finalNetWt) : 0,
  }));
}
